import { Head, router } from "@inertiajs/react"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { ListShell } from "@/components/admin/list-shell"
import { AppliedSegmentChip } from "@/components/crew/applied-segment-chip"
import { CrewFilterRail } from "@/components/crew/crew-filter-rail"
import { CrewListActiveFilterChips } from "@/components/crew/crew-list-active-filter-chips"
import { CrewListEmptyState } from "@/components/crew/crew-list-empty-state"
import { CrewListNoResultsPanel } from "@/components/crew/crew-list-no-results-panel"
import { CrewListPaginationFooter } from "@/components/crew/crew-list-pagination-footer"
import { CrewListSkeleton } from "@/components/crew/crew-list-skeleton"
import { CrewListTable } from "@/components/crew/crew-list-table"
import { CrewQuickStats } from "@/components/crew/crew-quick-stats"
import { SaveSegmentButton } from "@/components/crew/save-segment-button"
import { SegmentSelector } from "@/components/crew/segment-selector"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { useCrewListUrl } from "@/hooks/use-crew-list-url"
import AppLayout from "@/layouts/app-layout"
import type { CrewFilters, CrewIndexProps } from "@/types/crew"

/**
 * OKS-25 — Crew Roster + Quick Stats page (`/crew`).
 *
 * Composes the F5/F6/F7/F8/F9 pieces against the BE-locked
 * `CrewIndexProps` shape:
 *   - 3-tile quick-stats strip (always rendered, even on empty + error)
 *   - Filter rail (hidden in the empty state per UX Spec)
 *   - Active-filter chips (rendered above the table when ANY filter set)
 *   - Table or one of 4 state surfaces (empty / no-results / error /
 *     loading-during-partial-reload)
 *   - Pagination footer (rendered only when the table is rendered)
 *
 * **5 states** (UX Spec §States):
 *   - Loading — Inertia partial reload in flight (`router.on('start')`)
 *     surfaces `<CrewListSkeleton>` over the table region
 *   - Empty — `is_empty: true` → `<CrewListEmptyState>`; filter rail hidden
 *   - No-results — `is_empty: false && members.length === 0` →
 *     `<CrewListNoResultsPanel>`; filter rail interactive
 *   - Error — Inertia visit rejected (`router.on('error')`) → `<Alert>`
 *     replaces the table region; Retry → `router.reload()`
 *   - Offline — `<ListShell>` auto-renders the offline banner via
 *     `useOnline()`
 *
 * **Stats unfiltered guarantee** — the URL hook's `RELOAD_KEYS`
 * intentionally OMITS `stats`, so a partial reload can't even
 * theoretically refetch the stats block on a filter mutation. The
 * Vitest matrix has a dedicated assertion that filter-mutations leave
 * the rendered stats values unchanged.
 *
 * Locked testid: `crew-list-page` (`data-empty="true|false"`).
 */
export default function CrewIndex({
  members,
  stats,
  filters,
  sort,
  pagination,
  filter_options,
  is_empty,
  applied_segment,
  crew_segments,
}: CrewIndexProps) {
  const { t } = useTranslation()
  const url = useCrewListUrl(filters, sort)

  // ── Loading state — partial-reload skeleton ──────────────────────
  // `router.on("start")` fires on every Inertia visit (full + partial).
  // We flip the flag whenever a same-page partial reload is in flight
  // and clear it on `finish`. The skeleton replaces the table region
  // for the duration; stats + filter rail stay mounted (no flicker).
  const [loading, setLoading] = useState(false)

  // ── Error boundary — Inertia visit rejected ──────────────────────
  // Mirrors OKS-17 Phase-4 F1 fix: if a partial reload errors out,
  // surface the locked error testid + retry affordance instead of
  // leaving the page in a stale state. Cleared on the next visit's
  // `start`.
  const [hasError, setHasError] = useState(false)

  useEffect(() => {
    const offStart = router.on("start", () => {
      setLoading(true)
      setHasError(false)
    })
    const offFinish = router.on("finish", () => setLoading(false))
    const offError = router.on("error", () => {
      setHasError(true)
      setLoading(false)
    })
    return () => {
      offStart()
      offFinish()
      offError()
    }
  }, [])

  const showError = hasError
  const showLoading = loading && !showError
  const showEmpty = !showError && !showLoading && is_empty
  const showNoResults =
    !showError && !showLoading && !is_empty && members.length === 0
  const showTable =
    !showError && !showLoading && !is_empty && members.length > 0

  function handleRemoveFilter<K extends keyof CrewFilters>(
    key: K,
    value?: string,
  ) {
    if (key === "tags" && value) {
      url.setFilter(
        "tags",
        filters.tags.filter((entry) => entry !== value),
      )
    } else if (key === "followers_min" || key === "followers_max") {
      // Both followers params travel together — clear both at once.
      url.push({ followers_min: null, followers_max: null }, undefined, 1)
    } else if (key === "joined_from" || key === "joined_to") {
      url.push({ joined_from: null, joined_to: null }, undefined, 1)
    } else if (key === "q") {
      url.setFilter("q", null)
    } else if (key === "location") {
      // OKS-348 — Single composite-string `?location=` param.
      url.setFilter("location", null)
    } else if (key === "at_risk") {
      // OKS-27 — Boolean filter; remove via false. Default `false`
      // drops from URL via buildCrewListParams emission rule.
      url.setFilter("at_risk", false)
    } else if (
      key === "masterclass_completion_op" ||
      key === "masterclass_completion_id"
    ) {
      // OKS-58 — Both URL keys travel as a pair; remove BOTH at once.
      // `buildCrewListParams` mirrors the pair-emit invariant — passing
      // both `null` here drops the pair from the URL.
      url.push(
        {
          masterclass_completion_op: null,
          masterclass_completion_id: null,
        },
        undefined,
        1,
      )
    }
  }

  return (
    <AppLayout>
      <Head title={t("crew.list.head_title")} />
      <div
        data-testid="crew-list-page"
        data-empty={is_empty ? "true" : "false"}
      >
        <ListShell
          testId="crew-list-shell"
          title={t("crew.list.page_title")}
          subtitle={
            <Badge
              variant="secondary"
              data-testid="crew-list-member-count"
              data-count={stats.crew_size}
              aria-label={t("crew.list.member_count_aria", {
                count: stats.crew_size,
              })}
            >
              {stats.crew_size}
            </Badge>
          }
          toolbar={
            <div className="flex flex-col gap-3">
              <CrewQuickStats stats={stats} />
              {!showEmpty && (
                <>
                  {/*
                    OKS-28 — Segment selector. Sits ABOVE the filter
                    rail at ≥640px (UX Spec §2.1). At <640px the
                    selector collapses INTO the mobile filter Sheet
                    as the first row (UX Spec §5) — this desktop copy
                    is hidden via `sm:` and the mobile copy is passed
                    to `<CrewFilterRail mobileSheetTopSlot>`.
                  */}
                  <div className="hidden sm:block">
                    <SegmentSelector
                      segments={crew_segments}
                      appliedSegment={applied_segment}
                      onSegmentChange={url.setSegment}
                    />
                  </div>
                  <CrewFilterRail
                    filters={filters}
                    sort={sort}
                    filterOptions={filter_options}
                    onSearchChange={(q) => url.setFilter("q", q)}
                    onLocationChange={(value) =>
                      // OKS-348 — Single composite-string `?location=`
                      // URL param. Clearing sets it to null.
                      url.setFilter("location", value)
                    }
                    onFollowersChange={(min, max) =>
                      url.push(
                        { followers_min: min, followers_max: max },
                        undefined,
                        1,
                      )
                    }
                    onTagsChange={(tags) => url.setFilter("tags", tags)}
                    onJoinedRangeChange={(from, to) =>
                      url.push(
                        { joined_from: from, joined_to: to },
                        undefined,
                        1,
                      )
                    }
                    onAtRiskChange={(value) => url.setFilter("at_risk", value)}
                    onMasterclassCompletionChange={(op, masterclassId) =>
                      url.push(
                        {
                          masterclass_completion_op: op,
                          masterclass_completion_id: masterclassId,
                        },
                        undefined,
                        1,
                      )
                    }
                    onSortChange={url.setSort}
                    onClearAll={url.clearAll}
                    mobileSheetTopSlot={
                      <SegmentSelector
                        segments={crew_segments}
                        appliedSegment={applied_segment}
                        onSegmentChange={url.setSegment}
                      />
                    }
                    mobileSheetFooterSlot={
                      <SaveSegmentButton
                        filters={filters}
                        appliedSegment={applied_segment}
                        className="w-full"
                      />
                    }
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <CrewListActiveFilterChips
                      filters={filters}
                      onRemoveFilter={handleRemoveFilter}
                      onClearAll={url.clearAll}
                      // OKS-58 — Picker payload for the masterclass-
                      // completion chip's title lookup. Empty array
                      // when the brand has zero published mcs (the
                      // chip would never render in that case anyway,
                      // but we pass the prop unconditionally for type
                      // safety).
                      publishedMasterclasses={
                        filter_options.published_masterclasses
                      }
                    />
                    {/*
                      OKS-28 — Applied-segment chip + save-segment
                      button live alongside the OKS-25 filter chips
                      so the "applied state" reads as one rail. The
                      save button is desktop-only here (≥640px) — the
                      mobile copy lives in the Sheet footer (UX Spec §5).
                    */}
                    {applied_segment && (
                      <AppliedSegmentChip
                        segment={applied_segment}
                        onRemove={() => url.setSegment(null)}
                      />
                    )}
                    <div className="hidden sm:contents">
                      <SaveSegmentButton
                        filters={filters}
                        appliedSegment={applied_segment}
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
          }
          footer={
            showTable && (
              <CrewListPaginationFooter
                pagination={pagination}
                onPageChange={url.setPage}
              />
            )
          }
        >
          {showError && (
            <Alert
              variant="destructive"
              data-testid="crew-list-error"
              role="alert"
              className="flex flex-col gap-3 sm:flex-row sm:items-start"
            >
              <div className="flex-1">
                <AlertTitle>{t("crew.list.error.title")}</AlertTitle>
                <AlertDescription>
                  {t("crew.list.error.description")}
                </AlertDescription>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                data-testid="crew-list-error-retry"
                onClick={() => {
                  setHasError(false)
                  router.reload()
                }}
              >
                {t("crew.list.error.retry")}
              </Button>
            </Alert>
          )}
          {showLoading && <CrewListSkeleton />}
          {showEmpty && <CrewListEmptyState />}
          {showNoResults && (
            <CrewListNoResultsPanel onClearAll={url.clearAll} />
          )}
          {showTable && (
            <CrewListTable
              members={members}
              sort={sort}
              onSortChange={url.setSort}
            />
          )}
        </ListShell>
      </div>
    </AppLayout>
  )
}
